import { Injectable } from "@angular/core";
import moment from "moment";
import {
  BroadcastPresetShow,
  DynamicPromoShow,
  GraphicsShow,
  LayoutShow,
  LiveShow,
  MediaShow,
  Show,
  ShowMetaData,
} from "../../../domain/show";
import { HttpService } from "../../http/http";
import { HttpErrorResponse, HttpParams } from "@angular/common/http";
import { UserService } from "../../../domain/user-service";
import { ExportConfiguration } from "../../../domain/export-configuration";
import { Subject } from "rxjs";
import {
  ECopyShowsResult,
  EReplaceShowStrategy,
  ExtendedSubjectVoid,
  ShowRepository,
} from "../../../domain/show-repository";
import { ShowType } from "../../../domain/media";
import { MediaRepository } from "../../../domain/media-repository";
import { DynamicPromoInfo } from "../../../domain/dyanmicPromo";
import { DynamicPromoRepository } from "../../../domain/dynamic-promo-repository";
import { GraphicsRepository } from "../../../domain/graphics-repository";
import { SecEventType } from "../../../domain/sec-events";
import { OriginMetadata } from "../../../domain/origin-metadata";

@Injectable()
export class ShowRepositoryImpl implements ShowRepository {
  constructor(
    private httpService: HttpService,
    private userService: UserService,
    private mediaRepository: MediaRepository,
    private graphicsRepository: GraphicsRepository,
    private dynamicPromoRepository: DynamicPromoRepository,
  ) {}

  onShowsChange = new ExtendedSubjectVoid();

  init() {
    window.addEventListener("storage", this.handleStorageEvent.bind(this));
  }

  getByDates(
    channelId: number,
    startDate: moment.Moment,
    endDate: moment.Moment,
    shouldIncludeSecEvents: boolean = false,
    abortSubject: Subject<void> = null,
    includeLiveFeedLatencies = false,
    logResponse = false,
  ): Promise<Show[]> {
    return Promise.resolve()
      .then(() => {
        let abortReference = new Subject<void>();
        if (abortSubject) {
          abortReference = abortSubject;
        }
        const queryString = `ChannelId=${channelId}&StartDate=${startDate.clone().utc().format("YYYYMMDD")}&EndDate=${endDate.clone().utc().format("YYYYMMDD")}&StartTime=${startDate.clone().utc().format("HHmmssSSS")}&EndTime=${endDate.clone().utc().format("HHmmssSSS")}&ShouldIncludeSecEvents=${shouldIncludeSecEvents}&IncludeLiveFeedLatencies=${includeLiveFeedLatencies}&LogResponse=${logResponse}`;
        if (includeLiveFeedLatencies == false) {
          //console.trace(`show-repository->getByDates`, queryString);
        }

        return this.httpService.get(
          "Scheduler",
          {
            params: new HttpParams({
              fromString: queryString,
            }),
          },
          abortReference,
        );
      })
      .then((response) => {
        if (!response) {
          return undefined;
        }
        return response.map((show) =>
          this.mapShowResponse(show, channelId, shouldIncludeSecEvents),
        );
      });
  }
  async getNextShow(
    channelId: number,
    startDate: moment.Moment,
  ): Promise<Show | Error> {
    try {
      var utcStartDate = startDate.clone().utc();
      var nextShow = await this.httpService.get(
        `Scheduler/GetNextItem?ChannelId=${channelId}&StartDate=${utcStartDate.format("YYYYMMDD")}&StartTime=${utcStartDate.format("HHmmssSSS")}`,
      );
      return this.mapShowResponse(nextShow, channelId, false);
    } catch (err: any) {
      return new Error(err.error);
    }
  }
  getLiveBroadcastingState(channelId: number): Promise<Show[]> {
    return this.httpService
      .get(`Scheduler/GetLiveBroadcastingState?ChannelId=${channelId}`)
      .then((response) => {
        return response.map((showResponse) => {
          if (showResponse) {
            return this.mapShowResponse(showResponse, channelId, false);
          } else {
            return showResponse;
          }
        });
      });
  }

  mapShowResponse(
    show: any,
    channelId: number,
    shouldIncludeSecEvents: boolean,
  ): Show {
    {
      const duration = moment
        .utc(show["Duration"], ["HHmmssSSS", "HH:mm:ssSSS"], "UTC")
        .format("HH:mm:ss.SSS");
      let returnedShow: Show;

      const som = moment
        .utc(show["Som"], ["HHmmssSSS", "HH:mm:ssSSS"], "UTC")
        .format("HH:mm:ss.SSS");
      if (show.ShowType === ShowType.Media) {
        returnedShow = new MediaShow(
          show["ItemName"],
          channelId,
          show["ThumbnailURL"],
          moment.utc(show["StartTime"]),
          moment.duration(duration),
          show["ShowId"],
          show["MediaId"],
          show["CustomId"],
          moment.duration(som),
          (show["Subtitles"] || []).map((subtitle) => {
            return { id: subtitle.id, language: subtitle.language };
          }),
          false,
          moment.duration(show["MediaDuration"]),
          show["MediaStartTimestamp"],
          show["IsAnchored"],
          show["Origin"],
        );
      } else if (show.ShowType === ShowType.Live) {
        returnedShow = new LiveShow(
          show["ItemName"],
          channelId,
          show["ThumbnailURL"],
          moment.utc(show["StartTime"]),
          moment.duration(duration),
          show["ShowId"],
          show["FeedId"],
          show["LiveFeedLatencyMs"],
          show["IsAnchored"],
          show["Origin"],
        );
      } else if (show.ShowType === ShowType.DynamicPromo) {
        returnedShow = new DynamicPromoShow(
          show["ItemName"],
          channelId,
          show["ThumbnailURL"],
          moment.utc(show["StartTime"]),
          moment.duration(duration),
          show["ShowId"],
          show["DynamicPromoId"],
          moment.duration(show["MediaDuration"]),
          show["IsAnchored"],
          show["Origin"],
        );
      } else if (show.ShowType === ShowType.Layout) {
        returnedShow = new LayoutShow(
          show["ItemName"],
          channelId,
          show["ThumbnailURL"],
          moment.utc(show["StartTime"]),
          moment.duration(duration),
          show["ShowId"],
          show["LayoutUid"],
          show["IsAnchored"],
          show["Origin"],
        );
      } else if (show.ShowType === ShowType.BroadcastPreset) {
        returnedShow = new BroadcastPresetShow(
          show["ItemName"],
          channelId,
          show["ThumbnailURL"],
          moment.utc(show["StartTime"]),
          moment.duration(duration),
          show["ShowId"],
          show["BroadcastPresetUid"],
          show["IsAnchored"],
          show["Origin"],
        );
      } else if (show.ShowType === ShowType.GraphicsShow) {
        returnedShow = new GraphicsShow(
          show["ItemName"],
          channelId,
          show["ThumbnailURL"],
          moment.utc(show["StartTime"]),
          moment.duration(duration),
          show["ShowId"],
          show["GraphicInstanceUid"],
        );
      } else if (show.ShowType === ShowType.Contract) {
        returnedShow = new MediaShow(
          show["ItemName"],
          channelId,
          show["ThumbnailURL"],
          moment.utc(show["StartTime"]),
          moment.duration(duration),
          show["ShowId"],
          show["MediaId"],
          show["CustomId"],
          moment.duration(som),
          (show["Subtitles"] || []).map((subtitle) => {
            return { id: subtitle.id, language: subtitle.language };
          }),
          true,
          moment.duration(show["MediaDuration"]),
          0,
          show["IsAnchored"],
          show["Origin"],
        );
      }

      returnedShow.secEvents = [];
      if (shouldIncludeSecEvents) {
        show.AudioTracks.forEach((track) =>
          returnedShow.secEvents.push({
            id: track.id,
            type: SecEventType.Audio,
            properties: {
              id: track.id,
              language: track.Language,
              enabled: track.enabled,
            },
          }),
        );

        show.Subs.forEach((sub) =>
          returnedShow.secEvents.push({
            id: sub.id,
            type: SecEventType.Subtitles,
            properties: {
              id: sub.id,
              language: sub.Language,
              enabled: sub.enabled,
            },
          }),
        );

        (show.ClosedCaptions || []).forEach((cc) =>
          returnedShow.secEvents.push({
            id: cc.id,
            type: SecEventType.ClosedCaptions,
            properties: {
              id: cc.id,
              language: cc.Language,
              enabled: cc.enabled,
            },
          }),
        );

        show.AutopilotGraphics.forEach((item) =>
          returnedShow.secEvents.push({
            id: item.id,
            type: SecEventType.Autopilot,
            properties: this._updateMomentJsTimesAndDuration(item),
          }),
        );

        show.Graphics.forEach((item) =>
          returnedShow.secEvents.push({
            id: item.id,
            type: SecEventType.Graphics,
            textOverrides: item.textOverrides ?? [],
            properties: this._updateMomentJsTimesAndDuration(item),
          }),
        );

        show.FloatingMedia.forEach((item) =>
          returnedShow.secEvents.push({
            id: item.id,
            type: SecEventType.FloatingMedia,
            properties: this._updateMomentJsTimesAndDuration(item),
          }),
        );

        show.SCTE.forEach((item) =>
          returnedShow.secEvents.push({
            id: item.id,
            type: SecEventType.SCTE,
            properties: this._updateMomentJsTimesAndDuration(item),
          }),
        );
        // Uncomment once P2 should be released
        // show.CueTone.forEach(item => returnedShow.secEvents.push({type: SecEventType.Cuetone, properties: item}));

        returnedShow.secEvents.sort(function (a, b) {
          // @ts-ignore
          return a.properties.startTime - b.properties.startTime;
        });
      }
      if (show["CssStyle"]) {
        var style = JSON.parse(show["CssStyle"]);
        if (
          style != null &&
          typeof style === "object" &&
          !!Object.keys(style).length
        ) {
          returnedShow.cssStyles = style;
        }
      }
      if (show["OriginMetadata"]) {
        returnedShow.OriginMetadata = new OriginMetadata(
          show["OriginMetadata"]["OriginFilename"],
          show["OriginMetadata"]["OriginStartTime"],
        );
      }
      if (show["Comment"]) {
        returnedShow.comment = show["Comment"];
      }

      return returnedShow;
    }
  }

  closeGapAfter(show: Show) {
    return this.userService
      .getCurrentChannel()
      .then((channel) => {
        return this.httpService.post("Scheduler/CloseGaps", {
          ShowIds: [show.id],
          ChannelId: channel.id,
        });
      })
      .then((response) => {
        // return Promise.reject('');
        if (
          response.FailedCloseGapResponses &&
          response.FailedCloseGapResponses.length > 0
        ) {
          return Promise.reject("Error");
        }
        this.onShowsChange.next();
      });
  }

  createMediaShow(
    mediaId: number,
    startTime: moment.Moment,
    somMs?: number,
    durationMs?: number,
    showName?: string,
    isAnchor?: boolean,
    showMetadata?: ShowMetaData,
  ) {
    let currentChannel;

    return this.userService
      .getCurrentChannel()
      .then((channel) => {
        currentChannel = channel;
      })
      .then(() => {
        return this.createMediaShowForChannel(
          currentChannel.id,
          mediaId,
          startTime,
          somMs,
          durationMs,
          showName,
          isAnchor,
          showMetadata,
        );
      });
  }

  createMediaShowForChannel(
    channelId: number,
    mediaId: number,
    startTime: moment.Moment,
    somMs?: number,
    durationMs?: number,
    showName?: string,
    isAnchor?: boolean,
    showMetadata?: ShowMetaData,
  ) {
    return this.httpService
      .post("Scheduler/CreateMediaShow", {
        ChannelID: channelId,
        Date: startTime.clone().utc().format("YYYYMMDD"),
        Time: startTime.clone().utc().format("HHmmssSSS"),
        MediaId: mediaId,
        SomMs: somMs,
        DurationMs: durationMs,
        OverrideShowName: showName,
        ShowMetaDataRequest: showMetadata?.toCreateMediaShowRequest(),
        IsAnchor: isAnchor,
      })
      .then((response) => {
        this.onShowsChange.next();
      });
  }

  createLiveShow(
    feedId: string,
    startTime: moment.Moment,
    duration: moment.Duration,
  ) {
    let currentChannel;
    return this.userService
      .getCurrentChannel()
      .then((channel) => {
        currentChannel = channel;
      })
      .then(() => {
        return this.httpService.post("Scheduler/CreateLiveShow", {
          ChannelId: currentChannel.id,
          Date: startTime.clone().utc().format("YYYYMMDD"),
          Time: startTime.clone().utc().format("HHmmssSSS"),
          FeedId: feedId,
          DurationMs: duration.asMilliseconds(),
        });
      })
      .then((response) => {
        this.onShowsChange.next();
      });
  }

  createBroadcastPresetShow(
    broadcastPresetUid: string,
    startTime: moment.Moment,
    durationMs: number,
  ): Promise<void> {
    let currentChannel;
    return this.userService
      .getCurrentChannel()
      .then((channel) => {
        currentChannel = channel;
      })
      .then(() => {
        return this.httpService.post("Scheduler/CreateBroadcastPresetShow", {
          ChannelId: currentChannel.id,
          Date: startTime.clone().utc().format("YYYYMMDD"),
          Time: startTime.clone().utc().format("HHmmssSSS"),
          BroadcastPresetUid: broadcastPresetUid,
          DurationMs: durationMs,
        });
      })
      .then((response) => {
        this.onShowsChange.next();
      });
  }

  createLayoutShow(
    layoutUid: string,
    startTime: moment.Moment,
    durationMs: number,
  ): Promise<void> {
    let currentChannel;
    return this.userService
      .getCurrentChannel()
      .then((channel) => {
        currentChannel = channel;
      })
      .then(() => {
        return this.httpService.post("Scheduler/CreateLayoutShow", {
          ChannelId: currentChannel.id,
          Date: startTime.clone().utc().format("YYYYMMDD"),
          Time: startTime.clone().utc().format("HHmmssSSS"),
          LayoutUid: layoutUid,
          DurationMs: durationMs,
        });
      })
      .then((response) => {
        this.onShowsChange.next();
      });
  }

  createGraphicsShow(
    graphicsId: number,
    graphicsUid: string,
    startTime: moment.Moment,
  ) {
    let currentChannel;
    return this.userService
      .getCurrentChannel()
      .then((channel) => {
        currentChannel = channel;
      })
      .then(() => {
        return this.httpService.post("Scheduler/CreateGraphicsShow", {
          ChannelId: currentChannel.id,
          InstanceUId: graphicsUid,
          InstanceId: graphicsId,
          StartTime: startTime.clone().utc().format("YYYY-MM-DDTHH:mm:ss.SSS"),
        });
      })
      .then((response) => {
        this.onShowsChange.next();
      });
  }

  createDynamicPromoShow(
    dynamicPromoInfo: DynamicPromoInfo,
    startTime: moment.Moment,
  ) {
    return this.getDurationOfDynamicPromo(dynamicPromoInfo.id).then(
      (duration) => {
        let currentChannel;
        return this.userService
          .getCurrentChannel()
          .then((channel) => {
            currentChannel = channel;
          })
          .then(() => {
            return this.httpService.post("Scheduler/CreateDynamicPromoShow", {
              ChannelId: currentChannel.id,
              Date: startTime.clone().utc().format("YYYYMMDD"),
              Time: startTime.clone().utc().format("HHmmssSSS"),
              PromoId: dynamicPromoInfo.id,
              DurationMs: duration.asMilliseconds(),
            });
          })
          .then((response) => {
            this.onShowsChange.next();
          });
      },
    );
  }

  private getDurationOfDynamicPromo(
    dynamicPromoId: string,
  ): Promise<moment.Duration> {
    return new Promise((resolve, reject) => {
      return this.dynamicPromoRepository
        .getDynamicPromo(dynamicPromoId)
        .then((dynamicPromo) => {
          let duration;
          if (dynamicPromo.trailerId == null) {
            return this.graphicsRepository
              .getGraphics(dynamicPromo.graphicsId)
              .then((graphicInfo) => {
                console.log("graphicInfo", graphicInfo);
                duration = moment
                  .duration(graphicInfo.durationMS, "milliseconds")
                  .add(
                    moment.duration(
                      dynamicPromo.startGraphicOffsetSeconds,
                      "seconds",
                    ),
                  );

                resolve(duration);
              });
          } else {
            return this.mediaRepository
              .getMediaInfo(dynamicPromo.trailerId)
              .then((trailerInfo) => {
                duration = trailerInfo.contentDuration;

                resolve(duration);
              });
          }
        });
    });
  }

  updateShowComment(showId: string, comment: string): Promise<void> {
    let currentChannel;

    return new Promise(() => {
      return this.httpService.post("Scheduler/UpdateShowComment", {
        ShowId: showId,
        Comment: comment,
      });
    }).then(() => {
      this.onShowsChange.next();
    });
  }

  updateShowTime(showId: string, date: moment.Moment) {
    let currentChannel;

    return this.userService
      .getCurrentChannel()
      .then((channel) => {
        currentChannel = channel;
      })
      .then(() => {
        return this.httpService.post("Scheduler/UpdateShowTime", {
          ChannelId: currentChannel.id,
          ShowId: showId,
          NewDate: date.clone().utc().format("YYYYMMDD"),
          NewTime: date.clone().utc().format("HHmmssSSS"),
        });
      })
      .then(() => {
        this.onShowsChange.next();
      });
  }

  deleteShow(showId: string) {
    return this.deleteShows([showId]);
  }
  deleteShows(showIds: string[]): Promise<void> {
    let currentChannel;

    return this.userService
      .getCurrentChannel()
      .then((channel) => {
        currentChannel = channel;
      })
      .then(() => {
        return this.deleteChannelShows(currentChannel.id, showIds);
      });
  }

  deleteChannelShows(channelId: number, showIds: string[]): Promise<void> {
    return this.httpService
      .post("Scheduler/DeleteShows", {
        ChannelId: channelId,
        ShowIdsToDelete: showIds,
      })
      .then(() => {
        this.onShowsChange.next();
      });
  }
  exportEpg(
    channelId: number,
    startDate: moment.Moment,
    endDate: moment.Moment,
    minDuration: number,
    channelName: string,
    exportConfigId: number,
    exportFormat: string,
  ) {
    let filename;

    if (exportFormat === "xml") {
      filename = `${channelName}_${moment().format("YYMMDDHHmm")}.xml`;
    } else {
      filename =
        channelName +
        ":" +
        startDate.format("YYYYMMDD") +
        "-" +
        endDate.format("YYYYMMDD") +
        `.${exportFormat}`;
    }
    return this.httpService.getFile(
      `scheduler/exportepg?ChannelId=${channelId}\
        &MinDuration=${minDuration}\
        &StartDate=${moment.utc(startDate.valueOf()).format("YYYYMMDD")}\
        &EndDate=${moment.utc(endDate.valueOf()).format("YYYYMMDD")}\
        &ExportConfigId=${exportConfigId}&Format=${exportFormat}`,
      filename,
    );
  }

  exportRunLog(
    channelId: number,
    startDate: moment.Moment,
    endDate: moment.Moment,
    channelName: string,
    exportConfigId: number,
    exportAsMarinaXml,
  ) {
    const filename =
      channelName +
      "-" +
      startDate.format("YYYYMMDD") +
      "-" +
      endDate.format("YYYYMMDD") +
      (exportAsMarinaXml ? ".xml" : ".txt");
    let url = `scheduler/exportasrunlog?ChannelId=${channelId}\
        &StartDate=${moment.utc(startDate.valueOf()).format("YYYYMMDD")}\
        &EndDate=${moment.utc(endDate.valueOf()).format("YYYYMMDD")}\
        &ExportConfigId=${exportConfigId}`;
    if (exportAsMarinaXml) {
      url += "&IsKLHXmlFormat=true";
    }
    return this.httpService.getFile(url, filename);
  }

  updatePrerollMedia(
    channelId: number,
    prerollShowIds: string[],
    currentPlayingShowId: string,
    clientId: string,
  ): Promise<void> {
    return this.httpService.post("Scheduler/UpdatePrerollMedia", {
      ChannelId: channelId,
      PrerollShowUids: prerollShowIds,
      CurrentPlayingShowId: currentPlayingShowId,
      ClientId: clientId,
    });
  }

  clearChannelPrerollList(
    channelId: number
  ): Promise<void> {
    return this.httpService.post("Scheduler/ClearChannelPrerollList", {
      ChannelId: channelId
    });
  }

  getExportConfigurations(): Promise<ExportConfiguration[]> {
    return this.httpService
      .get("Scheduler/GetExportConfigurations")
      .then((response) => {
        return response.map((exportConfiguration) => {
          return new ExportConfiguration(
            exportConfiguration.Id,
            exportConfiguration.Name,
            JSON.parse(exportConfiguration.Headers),
          );
        });
      });
  }

  updateExportConfiguration(exportConfiguration: ExportConfiguration) {
    return this.httpService.put("Scheduler/UpdateExportConfiguration", {
      Id: exportConfiguration.id,
      Name: exportConfiguration.name,
      Headers: JSON.stringify(exportConfiguration.headers),
    });
  }

  updateLiveBroadcastStatus(channelId: number, isActive: boolean) {
    //return Promise.resolve();
    return this.httpService.post("Scheduler/UpdateLiveBroadcastStatus", {
      IsActive: isActive,
      ChannelId: channelId,
    });
  }

  addExportConfiguration(exportConfiguration: ExportConfiguration) {
    return this.httpService.post("Scheduler/CreateExportConfiguration", {
      Name: exportConfiguration.name,
      Headers: JSON.stringify(exportConfiguration.headers),
    });
  }

  updatePlayout(channelId) {
    return this.httpService.post("Scheduler/UpdateChannelPlayout", {
      ChannelId: channelId,
    });
  }

  handleStorageEvent(e: StorageEvent) {
    if (e.key === "broadcastItemsUpdated") {
      this.onShowsChange.next();
    }
  }

  replaceShow(
    showId: string,
    customMediaId: string,
    applyToAllShowsWithSameMedia: boolean,
    replaceStrategy: EReplaceShowStrategy,
    replaceMediaUntil?: moment.Moment,
  ) {
    return this.httpService
      .post("Scheduler/ReplaceShow", {
        ShowId: showId,
        CustomMediaId: customMediaId,
        ApplyToAllShowsWithSameMedia: applyToAllShowsWithSameMedia,
        EndDate:
          replaceMediaUntil &&
          replaceMediaUntil.clone().utc().format("YYYYMMDD"),
        EndTime:
          replaceMediaUntil &&
          replaceMediaUntil.clone().utc().format("HHmmssSSS"),
        ReplaceStrategy: replaceStrategy,
      })
      .then(() => {
        this.onShowsChange.next();
      })
      .catch((err) => {
        this.onShowsChange.next();
        throw err;
      });
  }

  renameShow(
    showId: string,
    newName: string,
    raiseChannelScheduleUpdatedEvent = true,
  ) {
    return this.httpService
      .post("Scheduler/RenameShow", {
        ShowId: showId,
        Name: newName,
        RaiseChannelScheduleUpdatedEvent: raiseChannelScheduleUpdatedEvent,
      })
      .then(() => {
        this.onShowsChange.next();
      });
  }

  updateShowDuration(
    showId: string,
    duration: moment.Moment | number,
    raiseChannelScheduleUpdatedEvent = true,
  ) {
    const durationMs =
      typeof duration === "number" ? duration : duration.valueOf();
    return this.httpService
      .post("Scheduler/UpdateShowDuration", {
        ShowId: showId,
        DurationMs: durationMs,
        RaiseChannelScheduleUpdatedEvent: raiseChannelScheduleUpdatedEvent,
      })
      .then(() => {
        this.onShowsChange.next();
      });
  }

  updateShowSom(
    showId: string,
    duration: number,
    raiseChannelScheduleUpdatedEvent = true,
  ) {
    return this.httpService
      .post("Scheduler/UpdateShowSom", {
        ShowId: showId,
        DurationMs: duration,
        RaiseChannelScheduleUpdatedEvent: raiseChannelScheduleUpdatedEvent,
      })
      .then(() => {
        this.onShowsChange.next();
      });
  }

  closeGaps(showIds: Array<string>) {
    return this.userService
      .getCurrentChannel()
      .then((channel) => {
        return this.httpService.post("Scheduler/CloseGaps", {
          ShowIds: showIds,
          ChannelId: channel.id,
        });
      })
      .then((response) => {
        this.onShowsChange.next(response.messages);
      });
  }

  private _updateMomentJsTimesAndDuration(item: any) {
    if (item.startTime) {
      item.startTime = moment.utc(item.startTime);
    }
    if (item.duration) {
      item.duration = moment.duration(item.duration);
    }
    if (item.offset) {
      item.offset = moment.duration(item.offset);
    }
    if (item.endTime) {
      item.endTime = moment.utc(item.endTime);
    }
    return item;
  }

  updateShowDisabledSecEvents(
    showId: string,
    disabledSecEventIds: Array<string>,
    shouldUpdateShows: boolean,
  ): Promise<void> {
    return this.httpService
      .post(`Scheduler/${showId}/UpdateShowDisabledSecEvents`, {
        DisabledSecEvents: disabledSecEventIds,
      })
      .then(() => {
        if (shouldUpdateShows) {
          this.onShowsChange.next();
        }
      });
  }

  copyShowsWithSecondaryEvents(
    targetChannelId: number,
    showFrom: Show,
    showTo: Show,
    startTime: moment.Moment,
    forceCreatingSecondaryEventCommandSettings: boolean,
    checkSecondaryEventsCommandSettings = true,
    copyContracts = true,
    repeatUntil: moment.Moment | undefined,
    copyPartial = false,
  ): Promise<ECopyShowsResult> {
    return new Promise<ECopyShowsResult>((resolve) => {
      const request: any = {
        CopyFromShowId: showFrom.id,
        CopyToShowId: showTo.id,
        TargetPlaylistStartDate: startTime.clone().utc().format("YYYYMMDD"),
        TargetPlaylistStartTime: startTime.clone().utc().format("HHmmssSSS"),
        TargetChannelId: targetChannelId,
        ForceSecondaryEventCommandsToCreate:
          forceCreatingSecondaryEventCommandSettings,
        CheckSecondaryEventsCommandSettings:
          checkSecondaryEventsCommandSettings,
        CopyContracts: copyContracts,
        copyPartial: copyPartial,
      };
      if (repeatUntil) {
        request.TargetPlaylistEndDate = repeatUntil
          .clone()
          .utc()
          .format("YYYYMMDD");
        request.TargetPlaylistEndTime = repeatUntil
          .clone()
          .utc()
          .format("HHmmssSSS");
      }
      return this.httpService
        .post(`Scheduler/CopyShowsWithSecondaryEvents`, request)
        .then((result) => {
          this.onShowsChange.next();

          resolve(ECopyShowsResult.OK);
        })
        .catch((e) => {
          if (
            e.message === "Secondary events command settings must be created!"
          ) {
            resolve(ECopyShowsResult.SecondaryEventCommandsMissing);
          } else if (e.message === "Can not push forward anchored shows") {
            resolve(ECopyShowsResult.OverlapAnchoredShows);
          } else {
            resolve(ECopyShowsResult.OverlapShowsForbidden);
          }
        });
    });
  }

  arrangeShows(
    targetChannelId: number,
    startTime: moment.Moment,
    endTime: moment.Moment,
    fireEvent: boolean,
  ): Promise<void> {
    return this.httpService
      .post(`Scheduler/ArrangeShows`, {
        ChannelId: targetChannelId,
        TargetPlaylistStartDate: startTime.clone().utc().format("YYYYMMDD"),
        TargetPlaylistStartTime: startTime.clone().utc().format("HHmmssSSS"),
        TargetPlaylistEndDate: endTime.clone().utc().format("YYYYMMDD"),
        TargetPlaylistEndTime: endTime.clone().utc().format("HHmmssSSS"),
      })
      .then((result) => {
        if (fireEvent) {
          //this.onShowsChange.next();
        }
      });
  }
  updateShowAnchorState(showId: string, state: boolean) {
    this.httpService
      .post("Scheduler/UpdateShowAnchorState", {
        ShowId: showId,
        State: state,
      })
      .then(() => {
        this.onShowsChange.next();
      });
  }
}
